import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle } from 'lucide-react';
import logo from '../assets/Nimbus_logo_only.png';
import { colors } from '../styles/colors';

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Récupérer le token depuis l'URL
  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (!tokenFromUrl) {
      setError('Lien de réinitialisation invalide');
    } else {
      setToken(tokenFromUrl);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (newPassword.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/.netlify/functions/resetPassword', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, newPassword }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        // Rediriger vers la page de connexion après 3 secondes
        setTimeout(() => {
          navigate('/login', { state: { message: 'Mot de passe réinitialisé avec succès. Vous pouvez maintenant vous connecter.' } });
        }, 3000);
      } else {
        setError(data.error || 'Une erreur est survenue');
      }
    } catch (err) {
      console.error('Erreur lors de la réinitialisation:', err);
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  // Afficher l'écran de succès
  if (success) {
    return (
      <div style={styles.container}>
        <div style={styles.logoContainer}>
          <img src={logo} alt="Nimbus Logo" style={styles.logo} />
        </div>

        <div style={styles.formContainer}>
          <div style={styles.successIconContainer}>
            <CheckCircle size={64} color={colors.primary.teal} />
          </div>
          <h1 style={styles.title}>Mot de passe réinitialisé !</h1>
          <p style={styles.subtitle}>
            Votre mot de passe a été réinitialisé avec succès.
          </p>
          <p style={styles.redirectMessage}>
            Redirection vers la page de connexion...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Logo */}
      <div style={styles.logoContainer}>
        <img src={logo} alt="Nimbus Logo" style={styles.logo} />
      </div>

      {/* Formulaire */}
      <div style={styles.formContainer}>
        <h1 style={styles.title}>Nouveau mot de passe</h1>
        <p style={styles.subtitle}>
          Choisissez un nouveau mot de passe pour votre compte.
        </p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.inputGroup}>
            <label htmlFor="newPassword" style={styles.label}>
              Nouveau mot de passe
            </label>
            <div style={{ position: 'relative' }}>
              <input
                id="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                style={styles.input}
                disabled={isLoading || !token}
                placeholder="Minimum 8 caractères"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                style={styles.eyeButton}
                disabled={isLoading || !token}
                tabIndex={-1}
              >
                {showNewPassword ? (
                  <EyeOff size={18} />
                ) : (
                  <Eye size={18} />
                )}
              </button>
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label htmlFor="confirmPassword" style={styles.label}>
              Confirmer le mot de passe
            </label>
            <div style={{ position: 'relative' }}>
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                style={styles.input}
                disabled={isLoading || !token}
                placeholder="Confirmez le mot de passe"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={styles.eyeButton}
                disabled={isLoading || !token}
                tabIndex={-1}
              >
                {showConfirmPassword ? (
                  <EyeOff size={18} />
                ) : (
                  <Eye size={18} />
                )}
              </button>
            </div>
          </div>

          {error && (
            <div style={styles.errorMessage}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading || !token}
            style={{
              ...styles.button,
              opacity: (isLoading || !token) ? 0.6 : 1,
              cursor: (isLoading || !token) ? 'not-allowed' : 'pointer'
            }}
          >
            {isLoading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
          </button>
        </form>
      </div>

      {/* Styles CSS */}
      <style>
        {`
          input:focus {
            outline: none;
            border-color: ${colors.primary.teal};
            box-shadow: 0 0 0 3px ${colors.primary.teal}20;
          }

          button:hover:not(:disabled) {
            background-color: ${colors.primary.teal};
            opacity: 0.9;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }

          button:active:not(:disabled) {
            transform: translateY(0);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          }
        `}
      </style>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.secondary.lightGray,
    fontFamily: '"Noto Sans", sans-serif',
    padding: '2rem'
  },
  logoContainer: {
    marginBottom: '2rem'
  },
  logo: {
    width: '120px',
    height: 'auto',
    display: 'block'
  },
  formContainer: {
    backgroundColor: colors.text.white,
    padding: '3rem',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    width: '100%',
    maxWidth: '450px'
  },
  title: {
    fontSize: '1.875rem',
    fontWeight: '700',
    color: colors.primary.gray,
    textAlign: 'center',
    marginBottom: '1rem',
    marginTop: 0
  },
  subtitle: {
    fontSize: '0.9375rem',
    color: colors.secondary.mediumGray,
    textAlign: 'center',
    marginBottom: '2rem',
    lineHeight: '1.5'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem'
  },
  label: {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: colors.primary.gray
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    fontSize: '1rem',
    border: `1px solid ${colors.secondary.mediumGray}`,
    borderRadius: '6px',
    transition: 'all 0.2s ease',
    fontFamily: '"Noto Sans", sans-serif',
    boxSizing: 'border-box'
  },
  eyeButton: {
    position: 'absolute',
    right: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.25rem',
    display: 'flex',
    alignItems: 'center',
    color: colors.secondary.mediumGray
  },
  button: {
    width: '100%',
    padding: '0.875rem',
    fontSize: '1rem',
    fontWeight: '600',
    color: colors.text.white,
    backgroundColor: colors.primary.teal,
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: '"Noto Sans", sans-serif',
    marginTop: '0.5rem'
  },
  errorMessage: {
    padding: '0.75rem',
    backgroundColor: '#fee',
    color: '#c33',
    borderRadius: '6px',
    fontSize: '0.875rem',
    textAlign: 'center',
    border: '1px solid #fcc'
  },
  successIconContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '1.5rem'
  },
  redirectMessage: {
    fontSize: '0.9375rem',
    color: colors.secondary.mediumGray,
    textAlign: 'center',
    marginTop: '1rem',
    fontStyle: 'italic'
  }
};

export default ResetPasswordPage;
